import moment from 'moment';

import {
  MINUTES_PER_HOUR,
  SCHEDULE_BLOCK_RANGE_TIME_FORMAT,
  SCHEDULE_BLOCK_TIME_FORMAT,
  SCHEDULE_END_HOUR,
  SCHEDULE_HOUR_LABEL_FORMAT,
  SCHEDULE_START_HOUR,
  SCHEDULE_TOOLTIP_TIME_FORMAT,
  SCHEDULE_TOTAL_HOURS,
} from '../constants';

import type { FWeeklyScheduleItem, PositionedScheduleItem, WeeklyScheduleDay } from '../types';

export function getScheduleHourLabels(format: string = SCHEDULE_HOUR_LABEL_FORMAT): string[] {
  const labels: string[] = [];

  for (let hour = SCHEDULE_START_HOUR; hour < SCHEDULE_END_HOUR; hour += 1) {
    labels.push(moment({ hour }).format(format));
  }

  return labels;
}

export function getPositionedScheduleItem(item: FWeeklyScheduleItem): PositionedScheduleItem {
  // Times are stored as timezone-agnostic wall-clock values (UTC), so read them in UTC to show the
  // same hour to every viewer regardless of their local timezone.
  const start = moment.utc(item.schedule_start_date);
  const end = moment.utc(item.schedule_end_date);
  const gridStartMinutes = SCHEDULE_START_HOUR * MINUTES_PER_HOUR;
  const gridTotalMinutes = SCHEDULE_TOTAL_HOURS * MINUTES_PER_HOUR;

  const rawEndMinutes = end.hour() * MINUTES_PER_HOUR + end.minute();
  // A midnight end (00:00) marks the end of the grid (24:00), not the start of the day.
  const normalizedEndMinutes =
    rawEndMinutes === 0 ? SCHEDULE_END_HOUR * MINUTES_PER_HOUR : rawEndMinutes;

  const startOffset = Math.min(
    Math.max(start.hour() * MINUTES_PER_HOUR + start.minute() - gridStartMinutes, 0),
    gridTotalMinutes,
  );
  const endOffset = Math.min(Math.max(normalizedEndMinutes - gridStartMinutes, 0), gridTotalMinutes);

  return {
    ...item,
    startHourOffset: startOffset / MINUTES_PER_HOUR,
    durationHours: (endOffset - startOffset) / MINUTES_PER_HOUR,
    startTimeLabel: start.format(SCHEDULE_BLOCK_TIME_FORMAT),
    endTimeLabel: end.format(SCHEDULE_BLOCK_TIME_FORMAT),
    rangeTimeLabel: `${start.format(SCHEDULE_BLOCK_RANGE_TIME_FORMAT)} to ${end.format(
      SCHEDULE_BLOCK_RANGE_TIME_FORMAT,
    )}`,
    tooltipTimeRangeLabel: `${start.format(SCHEDULE_TOOLTIP_TIME_FORMAT)} - ${end.format(
      SCHEDULE_TOOLTIP_TIME_FORMAT,
    )}`,
    isMultiHour: endOffset - startOffset > MINUTES_PER_HOUR,
  };
}

export function getScheduleBlockHourOffsets(durationHours: number): number[] {
  const hourOffsets: number[] = [];

  // Stop before durationHours so the last line is not drawn on the block's bottom edge.
  for (let hourOffset = 1; hourOffset < durationHours; hourOffset += 1) {
    hourOffsets.push(hourOffset);
  }

  return hourOffsets;
}

export function groupScheduleItemsByDay(
  values: ReadonlyArray<FWeeklyScheduleItem>,
): Map<WeeklyScheduleDay, PositionedScheduleItem[]> {
  const grouped = new Map<WeeklyScheduleDay, PositionedScheduleItem[]>();

  for (const item of values) {
    const dayItems = grouped.get(item.day_of_week) ?? [];
    dayItems.push(getPositionedScheduleItem(item));
    grouped.set(item.day_of_week, dayItems);
  }

  return grouped;
}
